import type {ExperimentAssignmentEnvelopeV1, ExperimentScope} from './store-types'

const MAX_EXPERIMENT_PATH_LENGTH = 512
const MAX_STRING_VALUE_LENGTH = 4096
const MAX_FEATURE_VARIABLES = 100
const MAX_ASSIGNMENT_CONTEXT_LENGTH = 16_384
const MAX_SERIALIZED_ENVELOPE_LENGTH = 256 * 1024

const unavailableScope: ExperimentScope = Object.freeze({status: 'unavailable'})
const mergeFailUnavailableScope: ExperimentScope = Object.freeze({
  ...unavailableScope,
  contextMergeFailed: true,
})

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function utf8Length(value: string): number {
  return new TextEncoder().encode(value).byteLength
}

function isWithinAssignmentContextLimit(value: string): boolean {
  return utf8Length(value) <= MAX_ASSIGNMENT_CONTEXT_LENGTH
}

function isWithinSerializedEnvelopeLimit(value: unknown): boolean {
  const serialized = JSON.stringify(value)
  return serialized !== undefined && utf8Length(serialized) <= MAX_SERIALIZED_ENVELOPE_LENGTH
}

function isFeatureVariable(value: unknown): value is string {
  return typeof value === 'string' && utf8Length(value) <= MAX_STRING_VALUE_LENGTH
}

/**
 * Merges opaque context tokens while keeping the first value seen for each variant.
 *
 * For example, `A:one;B:two` merged with `A:three;C:four` keeps `A:one`.
 */
export function mergeAssignmentContext(current: string, incoming: string): string {
  const entries = new Map<string, string>()
  for (const context of [current, incoming]) {
    for (const entry of context.split(';')) {
      if (!entry) continue
      const separator = entry.indexOf(':')
      const variant = separator === -1 ? entry : entry.slice(0, separator)
      if (!entries.has(variant)) entries.set(variant, entry)
    }
  }
  return [...entries.values()].join(';')
}

/**
 * Returns a scope with accumulated assignment context merged into it.
 *
 * Keeps first-seen values and reuses the scope when its context is unchanged.
 * If the merged context or envelope exceeds its size limit, returns an unavailable scope
 * marked contextMergeFailed so the store clears accumulated context when applying it.
 */
export function withAssignmentContext(scope: ExperimentScope, assignmentContext: string): ExperimentScope {
  if (scope.status === 'unavailable') return scope

  const mergedContext = mergeAssignmentContext(assignmentContext, scope.envelope.assignment.assignment_context)

  // If the merged context is unchanged, reuse the existing scope without re-validating.
  // Wire-provided scopes are already validated by decodeExperimentAssignment, so re-checking
  // envelope serialization size here is redundant work in the common case.
  if (scope.envelope.assignment.assignment_context === mergedContext) {
    return scope
  }

  // Context changed: create a new resolved scope with the updated context and validate limits.
  const resolvedScope = Object.freeze({
    status: 'available',
    envelope: Object.freeze({
      ...scope.envelope,
      assignment: Object.freeze({...scope.envelope.assignment, assignment_context: mergedContext}),
    }),
  })

  if (!isWithinAssignmentContextLimit(mergedContext) || !isWithinSerializedEnvelopeLimit(resolvedScope.envelope)) {
    return mergeFailUnavailableScope
  }

  return resolvedScope
}

/**
 * Compares complete scopes, including assignment context, not just experiment values.
 *
 * Initial app roots must agree on their received scopes before sharing the document store.
 * Navigation should compare effective scopes after context merging and size validation; a different
 * merged context is still a scope change even when the experiment values are unchanged.
 */
export function equivalentScopes(left: ExperimentScope, right: ExperimentScope): boolean {
  // Identical scope references are always equivalent; skip comparing their contents.
  if (left === right) return true
  if (left.status !== right.status) return false
  if (left.status === 'unavailable' && right.status === 'unavailable') {
    return left.contextMergeFailed === right.contextMergeFailed
  }
  if (left.status === 'unavailable' || right.status === 'unavailable') return false
  const leftAssignment = left.envelope.assignment
  const rightAssignment = right.envelope.assignment
  if (
    leftAssignment.assignment_context !== rightAssignment.assignment_context ||
    leftAssignment.data_version !== rightAssignment.data_version ||
    leftAssignment.failed !== rightAssignment.failed
  ) {
    return false
  }

  const leftVariables = Object.entries(leftAssignment.feature_variables)
  const rightVariables = Object.entries(rightAssignment.feature_variables)
  return (
    leftVariables.length === rightVariables.length &&
    leftVariables.every(([path, value]) => rightAssignment.feature_variables[path] === value)
  )
}

/** Returns an unavailable scope when any part of the untrusted assignment envelope is invalid or oversized. */
export function decodeExperimentAssignment(value: unknown): ExperimentScope {
  if (!isRecord(value) || value.schema_version !== 1 || !isRecord(value.assignment)) {
    return unavailableScope
  }

  const {assignment} = value
  if (
    !isRecord(assignment.feature_variables) ||
    typeof assignment.assignment_context !== 'string' ||
    !isWithinAssignmentContextLimit(assignment.assignment_context) ||
    typeof assignment.data_version !== 'number' ||
    !Number.isSafeInteger(assignment.data_version) ||
    assignment.data_version < 0 ||
    typeof assignment.failed !== 'boolean'
  ) {
    return unavailableScope
  }

  // Paths come from the wire, so a null prototype keeps names such as "__proto__" as ordinary data.
  const featureVariables = Object.create(null) as Record<string, string>
  const entries = Object.entries(assignment.feature_variables)
  if (entries.length > MAX_FEATURE_VARIABLES) return unavailableScope
  for (const [path, featureValue] of entries) {
    if (!path || utf8Length(path) > MAX_EXPERIMENT_PATH_LENGTH || !isFeatureVariable(featureValue)) {
      return unavailableScope
    }
    featureVariables[path] = featureValue
  }
  const envelope: ExperimentAssignmentEnvelopeV1 = Object.freeze({
    schema_version: 1,
    assignment: Object.freeze({
      feature_variables: Object.freeze(featureVariables),
      assignment_context: assignment.assignment_context,
      data_version: assignment.data_version,
      failed: assignment.failed,
    }),
  })
  if (!isWithinSerializedEnvelopeLimit(envelope)) return unavailableScope
  return Object.freeze({status: 'available', envelope})
}

/** Creates the fallback scope used while an experiment assignment is absent or still tentative. */
export function unavailableExperimentScope(): ExperimentScope {
  return unavailableScope
}
